import "@/App.css";
import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import { api, errMsg } from "./lib/api";
import { AppShell } from "./components/AppShell";
import Login from "./pages/Login";
import Blueprint from "./pages/Blueprint";
import SalesDashboard from "./pages/SalesDashboard";
import Leads from "./pages/Leads";
import LeadDetail from "./pages/LeadDetail";
import Accounts from "./pages/Accounts";
import AccountDetail from "./pages/AccountDetail";
import Employees from "./pages/hr/Employees";
import LeadsBin from "./pages/LeadsBin";
import Followups from "./pages/Followups";
import Leaderboard from "./pages/Leaderboard";
import Callbacks from "./pages/Callbacks";
import Brief from "./pages/Brief";
import WelcomeHome from "./pages/Home";
import Duplicates from "./pages/Duplicates";
import Approvals from "./pages/Approvals";
import Exports from "./pages/Exports";
import Locked from "./pages/Locked";
import ChangePassword from "./pages/ChangePassword";
import { ForgotPassword, ResetPassword } from "./pages/PasswordReset";
import OrderDashboard from "./pages/orders/OrderDashboard";
import Orders from "./pages/orders/Orders";
import OrderDetail from "./pages/orders/OrderDetail";
import DocLog from "./pages/orders/DocLog";
import AdminUsers from "./pages/admin/Users";
import AdminRoles from "./pages/admin/Roles";
import AdminAccessCheck from "./pages/admin/AccessCheck";
import AdminAudit from "./pages/admin/Audit";
import AdminMigration from "./pages/admin/Migration";
import AdminIntake from "./pages/admin/Intake";
import AdminWhatsApp from "./pages/admin/WhatsApp";
import AdminSettings from "./pages/admin/Settings";
import AdminAlerts from "./pages/admin/Alerts";
import MyStepsPage from "./pages/MySteps";
import OpsBoard from "./pages/ops/OpsBoard";
import OpsPlan from "./pages/ops/OpsPlan";
import MyOpsTasks from "./pages/ops/MyOpsTasks";
import OpsScores from "./pages/ops/OpsScores";
import OpsApprovals from "./pages/ops/OpsApprovals";
import OpsSettings from "./pages/ops/OpsSettings";
import OpsImport from "./pages/ops/OpsImport";
import OpsTemplates from "./pages/ops/OpsTemplates";
import BpDashboard from "./pages/bp/BpDashboard";
import BranchPlan from "./pages/bp/BranchPlan";
import MyPlan from "./pages/bp/MyPlan";
import WeeklyReview from "./pages/bp/WeeklyReview";

const AuthCallback = () => {
  const done = useRef(false);
  const nav = useNavigate();
  const { refresh } = useAuth();
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
    api.post("/auth/session", { session_id: sid })
      .then(async () => { window.history.replaceState(null, "", window.location.pathname); await refresh(); nav("/", { replace: true }); })
      .catch((e) => { window.history.replaceState(null, "", "/login"); nav("/login", { replace: true, state: { error: errMsg(e) } }); });
  }, [nav, refresh]);
  return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Verifying your access…</div>;
};

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground" data-testid="auth-loading">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password && window.location.pathname !== "/account/password") return <Navigate to="/account/password" replace />;
  return children;
};

const Home = () => {
  const { can } = useAuth();
  if (can("sales", "read")) return <WelcomeHome />;
  if (can("operations", "read")) return <Navigate to="/ops/my-tasks" replace />;
  if (can("hr", "read")) return <Navigate to="/hr/employees" replace />;
  if (can("admin", "read")) return <Navigate to="/admin/users" replace />;
  return <Navigate to="/blueprint" replace />;
};

const AppRouter = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/" element={<Protected><AppShell /></Protected>}>
        <Route index element={<Home />} />
        <Route path="blueprint" element={<Blueprint />} />
        <Route path="sales/dashboard" element={<SalesDashboard />} />
        <Route path="sales/leads" element={<Leads />} />
        <Route path="sales/leads/:id" element={<LeadDetail />} />
        <Route path="sales/accounts" element={<Accounts />} />
        <Route path="sales/accounts/:id" element={<AccountDetail />} />
        <Route path="hr/employees" element={<Employees />} />
        <Route path="sales/followups" element={<Followups />} />
        <Route path="sales/leaderboard" element={<Leaderboard />} />
        <Route path="sales/callbacks" element={<Callbacks />} />
        <Route path="brief" element={<Brief />} />
        <Route path="sales/duplicates" element={<Duplicates />} />
        <Route path="sales/approvals" element={<Approvals />} />
        <Route path="sales/deleted" element={<LeadsBin />} />
        <Route path="sales/exports" element={<Exports />} />
        <Route path="admin/exports" element={<Exports />} />
        <Route path="admin/users" element={<AdminUsers />} />
        <Route path="admin/access-check" element={<AdminAccessCheck />} />
        <Route path="admin/roles" element={<AdminRoles />} />
        <Route path="admin/audit" element={<AdminAudit />} />
        <Route path="admin/migration" element={<AdminMigration />} />
        <Route path="admin/intake" element={<AdminIntake />} />
        <Route path="admin/whatsapp" element={<AdminWhatsApp />} />
        <Route path="admin/settings" element={<AdminSettings />} />
        <Route path="admin/alerts" element={<AdminAlerts />} />
        <Route path="my-steps" element={<MyStepsPage />} />
        <Route path="account/password" element={<ChangePassword />} />
        <Route path="orders/dashboard" element={<OrderDashboard />} />
        <Route path="orders/list" element={<Orders />} />
        <Route path="orders/uid/:uid" element={<OrderDetail />} />
        <Route path="orders/document-log" element={<DocLog />} />
        <Route path="ops" element={<OpsBoard />} />
        <Route path="ops/uid/:uid" element={<OpsPlan />} />
        <Route path="ops/my-tasks" element={<MyOpsTasks />} />
        <Route path="ops/scores" element={<OpsScores />} />
        <Route path="ops/approvals" element={<OpsApprovals />} />
        <Route path="ops/settings" element={<OpsSettings />} />
        <Route path="ops/import" element={<OpsImport />} />
        <Route path="ops/templates" element={<OpsTemplates />} />
        <Route path="bp" element={<BpDashboard />} />
        <Route path="bp/branch" element={<BranchPlan />} />
        <Route path="bp/my-plan" element={<MyPlan />} />
        <Route path="bp/week" element={<WeeklyReview />} />
        <Route path="locked/:mod" element={<Locked />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
};

const useToastSpot = () => {
  const q = "(max-width: 767px)";
  const [phone, setPhone] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const on = (e) => setPhone(e.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return phone ? "bottom-center" : "top-right";
};

export default function App() {
  const spot = useToastSpot();
  return (
    <AuthProvider>
      <BrowserRouter><AppRouter /></BrowserRouter>
      <Toaster theme="light" richColors={false} position={spot} />
    </AuthProvider>
  );
}
